"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useStencilPopper = void 0;
var _react = require("react");
var _core = require("@popperjs/core");
var _reactFastCompare = _interopRequireDefault(require("react-fast-compare"));
var _comparisons = require("../utils/comparisons");
var _useIsomorphicLayoutEffect = require("./useIsomorphicLayoutEffect");
/*
The MIT License (MIT)

Copyright (c) 2018 React Popper authors

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

Forked from the react-popper v2.2.5 github repo (https://github.com/popperjs/react-popper)
Optimized to solve re-rendering issue (https://t.corp.amazon.com/P49329854/communication)
*/

const useStencilPopper = function (referenceElement, popperElement) {
  let isOpen = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  const optionsWithDefaults = {
    modifiers: options.modifiers || [],
    onFirstUpdate: options.onFirstUpdate,
    placement: options.placement || 'bottom',
    strategy: options.strategy || 'absolute'
  };
  // State to trigger useEffect if referenced components aren't null yet
  const [trigger, setTrigger] = (0, _react.useState)(false);
  const [styles, setStyles] = (0, _react.useState)({
    arrow: {
      position: 'absolute'
    },
    popper: {
      position: optionsWithDefaults.strategy,
      left: '0',
      top: '0'
    }
  });
  const [placement, setPlacement] = (0, _react.useState)(optionsWithDefaults.placement);
  const prevOptions = (0, _react.useRef)(null);
  const prevStyles = (0, _react.useRef)(null);
  const dynamicPhase = (0, _react.useRef)('beforeWrite');
  const popperInstanceRef = (0, _react.useRef)(null);

  // refactored modifier to deeply check old and new styles object to update local state
  const updateStateModifier = (0, _react.useMemo)(() => ({
    enabled: true,
    fn: _ref => {
      let {
        state
      } = _ref;
      if (state && !state.elements) return;

      // set placement from instance's state to have fresh position
      if (placement !== state.placement && state.modifiersData.isOpen) {
        setPlacement(state.placement);
      }
      const newStyles = Object.keys(state.elements).map(element => [element, state.styles[element] || {}]).reduce((acc, _ref2) => {
        let [key, value] = _ref2;
        acc[key] = value;
        return acc;
      }, {});

      // update only params to avoid frequent updates
      if (prevStyles.current === null || !(0, _comparisons.isDeepEqual)(prevStyles.current, newStyles)) {
        setStyles(newStyles);
      }
      prevStyles.current = newStyles;

      // using "beforeWrite" phase to solve initial set up
      // then set to default "write" phase that tracks scroll/resize events
      if (dynamicPhase.current === 'beforeWrite') {
        dynamicPhase.current = 'write';
      }
    },
    name: 'updateState',
    options: {
      placement
    },
    phase: dynamicPhase.current,
    requires: ['computeStyles']
  }), [dynamicPhase, placement, setStyles]);
  const popperOptions = (0, _react.useMemo)(() => {
    const eventListenerOptions = {
      resize: false,
      scroll: true
    };
    const newOptions = {
      onFirstUpdate: optionsWithDefaults.onFirstUpdate,
      placement: optionsWithDefaults.placement,
      strategy: optionsWithDefaults.strategy,
      modifiers: [...optionsWithDefaults.modifiers, {
        ...updateStateModifier,
        enabled: isOpen
      }, {
        name: 'applyStyles',
        phase: 'afterRead',
        enabled: true
      }, {
        ..._core.eventListeners,
        enabled: isOpen,
        options: eventListenerOptions
      }]
    };
    if (prevOptions.current && (0, _reactFastCompare.default)(prevOptions.current, newOptions)) {
      return prevOptions.current || newOptions;
    } else {
      prevOptions.current = newOptions;
      return newOptions;
    }
  }, [optionsWithDefaults.onFirstUpdate, optionsWithDefaults.strategy, optionsWithDefaults.modifiers, optionsWithDefaults.placement, updateStateModifier, isOpen]);

  // set new options passed in argument
  (0, _useIsomorphicLayoutEffect.useIsomorphicLayoutEffect)(() => {
    if (popperInstanceRef.current) {
      popperInstanceRef.current.setOptions(popperOptions);
    }
  }, [popperOptions]);

  // Initialize Popper instance
  (0, _useIsomorphicLayoutEffect.useIsomorphicLayoutEffect)(() => {
    if (referenceElement == null || popperElement == null) {
      setTrigger(true);
      return;
    }
    const createPopper = options.createPopper || _core.createPopper;
    const popperInstance = createPopper(referenceElement, popperElement, popperOptions);

    // initially isOpen should be true
    popperInstance.state.modifiersData.isOpen = true;
    popperInstanceRef.current = popperInstance;
    // To fix: https://t.corp.amazon.com/D104449106
    // When dropdown is open and window resizes, computes position correctly
    let timer;
    const forceUpdate = () => {
      clearTimeout(timer);
      if (isOpen) {
        timer = setTimeout(() => {
          popperInstance.forceUpdate();
        }, 0);
      }
    };
    window.addEventListener('resize', forceUpdate);

    // Add IntersectionObserver for dynamic position change when referenceElement intersection is changed
    let iObserver = null;
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      iObserver = new IntersectionObserver(() => {
        forceUpdate();
      });
      iObserver.observe(referenceElement);
    }
    return () => {
      popperInstance.destroy();
      popperInstanceRef.current = null;
      window.removeEventListener('resize', forceUpdate);

      // Cleanup IntersectionObserver
      if (iObserver && referenceElement && typeof iObserver.unobserve === 'function') {
        iObserver.unobserve(referenceElement);
        iObserver.disconnect();
      }
    };
  }, [referenceElement, popperElement, options.createPopper, trigger]);

  // update instance state isOpen
  (0, _useIsomorphicLayoutEffect.useIsomorphicLayoutEffect)(() => {
    if (popperInstanceRef.current) {
      popperInstanceRef.current.state.modifiersData.isOpen = isOpen;
      popperInstanceRef.current.forceUpdate();
    }
  }, [popperInstanceRef, isOpen]);
  return {
    forceUpdate: popperInstanceRef.current ? popperInstanceRef.current.forceUpdate : null,
    placement,
    state: popperInstanceRef.current ? popperInstanceRef.current.state : null,
    styles: styles || {},
    update: popperInstanceRef.current ? popperInstanceRef.current.update : null
  };
};
exports.useStencilPopper = useStencilPopper;